import { computed } from "vue";
import { useCompaniesStore } from "~/store/companies/companies.store";
import { CurrencyEnum, CURRENCY_SYMBOLS } from "~/utils/enums/currency.enums";

// Guard against a currency outside our known set: an unsupported code passed to
// Intl.NumberFormat throws and would break every price on the page. One formatter
// per currency, shared across all useCurrency() callers.
const SUPPORTED_CURRENCIES = new Set<string>(Object.values(CurrencyEnum));
const formatterCache = new Map<CurrencyEnum, Intl.NumberFormat>();
function getFormatter(currency: CurrencyEnum): Intl.NumberFormat {
  let formatter = formatterCache.get(currency);
  if (!formatter) {
    formatter = new Intl.NumberFormat("fr-FR", { style: "currency", currency });
    formatterCache.set(currency, formatter);
  }
  return formatter;
}

export const useCurrency = () => {
  // Resolve the store lazily inside the computed rather than at call time. useCurrency()
  // is reached at module scope via useFormats() in some stores (e.g. sessions-store.ts),
  // which is evaluated before app.use(pinia) — calling useCompaniesStore() there throws
  // "getActivePinia() was called but there was no active Pinia". The getter only runs on
  // read (render time), when Pinia is active.
  const currency = computed<CurrencyEnum>(() => {
    const raw = useCompaniesStore().companyInfos?.currency;
    return raw && SUPPORTED_CURRENCIES.has(raw) ? raw : CurrencyEnum.Eur;
  });

  const currencySymbol = computed<string>(() => CURRENCY_SYMBOLS[currency.value]);

  // Strip Intl's verbose currency literal so we can append the shorter symbol from CURRENCY_SYMBOLS.
  function formatPrice(amount: number | null | undefined): string {
    const numberOnly = getFormatter(currency.value)
      .formatToParts(amount ?? 0)
      .filter((p) => p.type !== "currency")
      .map((p) => p.value)
      .join("")
      .trim();

    return `${numberOnly} ${currencySymbol.value}`;
  }

  return {
    currency,
    currencySymbol,
    formatPrice,
  };
};
